import * as React from 'react';
import styled from 'styled-components';
import { UnitDiv, DescriptionDiv, HrHorizontal, Spinner, ValueDiv } from './Styles';
import { ReactComponent as ArrowUp } from '../../assets/icons/ArrowUp.svg';
import { notUndefiend, valueAsString } from 'helpers/conversions';
import { appearanceEffect, NoDataIconWrapper } from 'globalStyle';
import { UserSystemGroup, UserSystemGroupStrings } from 'store/storeTypes';
import EmptyStateIcon, { NO_DATA_FOR_PERIOD_MESSAGE } from 'components/_common/EmptyState';
import { AddedDataAmount, AddedDataUnit, AddedDataType, CardStats } from './Card.styles';

export const StatsData = (percentage: number) => {
  const color = percentage >= 0 ? '#1BB934' : '#FF4081';
  const sign = percentage > 0 ? '+' : '';
  return (
    <div style={{ color: color, marginRight: '5px' }}>
      {sign}
      {percentage && (percentage * 100).toFixed()}%
    </div>
  );
};
type Data = {
  value: number | Date | undefined;
  unit?: string;
  description: string;
  trend: number | undefined;
  formatter?: (value: number | string | undefined | Record<string, unknown> | Date) => string;
};
export interface ICardProps {
  //*null means the response arrived with no data for the period, undefined - still loading
  value: number | null | undefined;
  units: string;
  description: string;
  icons: { positive: React.ReactElement; negative?: React.ReactElement };
  stats?: number | undefined;
  statsDescription?: string;
  addedData?: {
    upper: Data;
    lower?: Data;
  };
  isPercentage?: boolean;
  cardName?: string;
  showOnlyBody?: boolean;
  width?: number;
  selectedSystemGroup?: UserSystemGroupStrings;
  isHidden?: boolean;
}

const AddedData = (
  value: string,
  description: string,
  trend: number | undefined,
  unit?: string,
  cardName?: string,
  upOrBottom?: string,
  upOrBottomArrow?: string
) => {
  return (
    <div style={{ minWidth: '105px' }}>
      <div
        style={{
          display: 'flex',
          marginBottom: '3px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <div style={{ display: 'flex' }}>
          <AddedDataAmount data-testid={`overview-card-${upOrBottom}`}>{value}</AddedDataAmount>
          {unit && <AddedDataUnit>{unit}</AddedDataUnit>}
        </div>
        {trend !== 0 && trend !== undefined && (
          <ArrowUp
            data-testid={`overview-card-${upOrBottomArrow}`}
            width={14}
            height={18}
            style={{ transform: trend < 0 ? 'rotate(180deg)' : '' }}
          />
        )}
      </div>
      <AddedDataType>{description}</AddedDataType>
    </div>
  );
};
const InnerCardWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding: 12px;
  justify-content: space-between;
  animation-name: ${appearanceEffect};
  animation-duration: 500ms;
`;
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const UpperRow = styled.div``;
const BottomRow = styled.div``;
const RightColumn = styled.div``;
const CardDiv = styled.div<{
  width: number;
  bigger?: boolean;
  height?: number;
  align?: boolean;
  isMobile?: boolean;
  selectedSystemGroup?: string;
  order?: number;
  isHidden?: boolean;
}>`
  background: ${props => (props.isHidden ? 'transparent' : '#ffffff')};
  border: ${props => (props.isHidden ? 'none' : '1px solid #c5d0de')};
  box-sizing: border-box;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  height: 100%;
  width: ${props => props.width}%;

  align-items: ${props => (props.align === undefined ? 'center' : '')};
  margin-bottom: ${props => (props.isMobile === true ? '10px' : '')};
`;
export default function Card(props: ICardProps) {
  const {
    value,
    units,
    description,
    icons,
    stats,
    statsDescription,
    addedData,
    isPercentage,
    cardName,
    showOnlyBody,
    width,
    selectedSystemGroup,
    isHidden,
  } = props;
  const isNegative = React.useMemo(() => {
    if (!notUndefiend([stats])) {
      return false;
    }
    if (stats! < 0) {
      return true;
    }
    if (description === 'Average Availability' && typeof value === 'number' && value >= 0.999) {
      return false;
    }
    return false;
  }, [description, stats, value]);

  return (
    <CardDiv width={width} isHidden={isHidden}>
      {selectedSystemGroup === UserSystemGroup.Apollo && cardName === 'availability' ? (
        <NoDataIconWrapper>
          <EmptyStateIcon icon="no-icon" message="Under Construction" />
        </NoDataIconWrapper>
      ) : value === null ? (
        <NoDataIconWrapper>
          <EmptyStateIcon icon="no-icon" message={NO_DATA_FOR_PERIOD_MESSAGE} />
        </NoDataIconWrapper>
      ) : notUndefiend([value]) ? (
        <InnerCardWrapper data-testid="top-card-overview">
          <LeftColumn>
            <UpperRow>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'start',
                }}>
                {isNegative ? icons.negative : icons.positive}
                <div style={{ display: 'flex', alignItems: 'baseline', marginLeft: '8px' }}>
                  <ValueDiv data-testid="overview-card-total-value">
                    {valueAsString(value, isPercentage)}
                  </ValueDiv>
                  <UnitDiv>{units}</UnitDiv>
                </div>
              </div>
              <DescriptionDiv>{description}</DescriptionDiv>
            </UpperRow>

            {notUndefiend([stats]) && !showOnlyBody && (
              <BottomRow>
                <HrHorizontal />
                <CardStats data-testid="overview-card-from-last-week">
                  {StatsData(stats!)}
                  <div>{statsDescription}</div>
                </CardStats>
              </BottomRow>
            )}
          </LeftColumn>
          {!!addedData && selectedSystemGroup !== UserSystemGroup.Apollo && (
            <RightColumn>
              {AddedData(
                addedData.upper.formatter
                  ? addedData.upper.formatter(addedData.upper.value)
                  : valueAsString(addedData.upper.value!),
                addedData.upper.description,
                addedData.upper.trend,
                addedData.upper.unit,
                cardName,
                'upperValue',
                'upperArrow'
              )}
              <HrHorizontal />
              {addedData.lower &&
                AddedData(
                  addedData.lower.formatter
                    ? addedData.lower.formatter(addedData.lower.value)
                    : valueAsString(addedData.lower.value!),
                  addedData.lower.description,
                  addedData.lower.trend,
                  addedData.lower.unit,
                  cardName,
                  'bottomValue',
                  'bottomArrow'
                )}
            </RightColumn>
          )}
        </InnerCardWrapper>
      ) : (
        Spinner(30)
      )}
    </CardDiv>
  );
}
